import { DataStore } from '../../data/store';
import { DataSet } from '../../design/charts';
import { Params } from '../../types';
import { DEFAULT_QUANTITY } from '../constants';
import { createChartData } from '../data/chartData';
import { ChartData, ColorHelper, createMarketKpi, findDescriptionColumn } from '../data/common';
import { formatChartSharedBrands } from '../utils';

export type SeriesAndSummaryData = {
  series: ChartData;
  summary?: ChartData;
};

export type RowDataSets = {
  uniqueId: string;
  label: string;
  seriesSet: DataSet;
  summarySet?: DataSet;
};

const GROWTH_KPI_MAP = (quantityType: string) => {
  const kpiValue = createMarketKpi(quantityType);

  return {
    [kpiValue.shareRevenue]: kpiValue.revenue,
    [kpiValue.shareQuantity]: kpiValue.quantity,
    [kpiValue.priceIndex]: kpiValue.averagePrice,
  };
};

export function makeTimeSeriesData(
  data: DataStore,
  params: Params,
  options: {
    xColumnName: string;
    yColumnName: string;
    sharedBrands?: string[];
  }
): { series: ChartData; summary?: ChartData; exportSeries: ChartData[] } | undefined {
  const { xColumnName, yColumnName, sharedBrands } = options;
  const quantityType = params?.quantity_type?.[0] ?? DEFAULT_QUANTITY;

  const columns = data.getDataColumns();

  const xColumn = findDescriptionColumn(columns, xColumnName);

  const yColumn = columns.find(col => col.name === yColumnName);
  if (!xColumn || !yColumn) {
    return;
  }

  const updatedYColumnName = GROWTH_KPI_MAP(quantityType)[yColumnName] || yColumnName;
  const updatedYColumn = columns.find(col => col.name === updatedYColumnName);

  const totalsGroupedData = data.getGroupedData([]);
  const totalsSeriesData = totalsGroupedData.length === 1 ? totalsGroupedData[0] : totalsGroupedData[1];
  const totalsSummaryData = totalsGroupedData.length === 1 ? undefined : totalsGroupedData[0];

  const xGroupedData = data.getGroupedData([xColumnName]);
  const xSeriesData = xGroupedData.length === 1 ? xGroupedData[0] : xGroupedData[1];
  const xSummaryData = xGroupedData.length === 1 ? undefined : xGroupedData[0];

  const xSummaryColumnNames = xSummaryData ? xSummaryData.data.map(item => item.values[xColumn.name]) : [];
  const xSeriesColumnNames = xSeriesData.data.map(item => item.values[xColumn.name]);

  const columnNames = Array.from(new Set([...xSummaryColumnNames, ...xSeriesColumnNames]));
  const includeTotals = ((totalsSeriesData?.data[0]?.values[updatedYColumnName] as Array<number | null>) || [])?.some(
    item => item !== null
  );

  formatChartSharedBrands(xColumnName, xSeriesData, sharedBrands);

  const colorHelper = new ColorHelper(
    xColumnName,
    columnNames.map(cn => String(cn))
  );

  const seriesData = createChartData(
    { rows: xSeriesData.data, columns, totals: totalsSeriesData?.data[0] },
    {
      xColumnName,
      yColumnName,
      currency: data.getCurrency(),
      includeTotals,
      setPerRow: true,
      colorHelper,
      updatedYColumn,
    }
  );

  if (!seriesData) {
    return;
  }
  seriesData.dataLabels = xSeriesData.timeFrame.periods.map(v => (Array.isArray(v.name) ? v.name : v.name.split(' ')));

  const exportSeriesData = splitChartData(seriesData);

  let summaryData: ChartData | undefined;
  if (totalsSummaryData && xSummaryData) {
    summaryData = createChartData(
      { rows: xSummaryData.data, columns, totals: totalsSummaryData.data[0] },
      {
        yColumnName,
        xColumnName,
        currency: data.getCurrency(),
        includeTotals,
        setPerRow: true,
        colorHelper,
        updatedYColumn,
      }
    );
    summaryData.dataLabels = xSummaryData.timeFrame.periods.map(v => v.name);
  }

  return {
    series: seriesData,
    summary: summaryData,
    exportSeries: exportSeriesData,
  };
}

function splitChartData(chartData: ChartData): ChartData[] {
  if (!chartData.hasData) {
    return [];
  }

  const chunks: ChartData[] = [];
  const chunkSize = 13; // Size of each chunk

  const { totalsDataSet, dataLabels, dataSets } = chartData;

  const rawValuesLength = dataSets[0].values.raw.length;

  for (let i = 0; i < rawValuesLength; i += chunkSize) {
    const chunkedData: ChartData = {
      ...chartData,
      dataSets: dataSets.map(dataSet => {
        const {
          values: { raw, formatted, fillColor, textColor },
          changes,
        } = dataSet;

        return {
          ...dataSet,
          values: {
            raw: raw.slice(i, i + chunkSize),
            formatted: formatted.slice(i, i + chunkSize),
            fillColor: fillColor.slice(i, i + chunkSize),
            textColor: textColor.slice(i, i + chunkSize),
          },
          changes: changes && {
            raw: changes.raw.slice(i, i + chunkSize),
            formatted: changes.formatted.slice(i, i + chunkSize),
            textColor: changes.textColor.slice(i, i + chunkSize),
          },
        };
      }),
      totalsDataSet: totalsDataSet && {
        ...totalsDataSet,
        changes: totalsDataSet.changes && {
          raw: totalsDataSet.changes.raw.slice(i, i + chunkSize),
          formatted: totalsDataSet.changes.formatted.slice(i, i + chunkSize),
          textColor: totalsDataSet.changes.textColor.slice(i, i + chunkSize),
        },
        values: {
          raw: totalsDataSet.values.raw.slice(i, i + chunkSize),
          formatted: totalsDataSet.values.formatted.slice(i, i + chunkSize),
          fillColor: totalsDataSet.values.fillColor.slice(i, i + chunkSize),
          textColor: totalsDataSet.values.textColor.slice(i, i + chunkSize),
        },
      },
      dataLabels: dataLabels.slice(i, i + chunkSize),
    };

    chunks.push(chunkedData);
  }

  return chunks;
}

export function getRowDataSets(chartData: SeriesAndSummaryData): RowDataSets[] {
  return chartData.series.dataSets.map(set => {
    const summarySet = chartData.summary?.dataSets.find(
      summarySet =>
        (set.uniqueId && summarySet.uniqueId === set.uniqueId) || (set.label && summarySet.label === set.label)
    );
    return {
      uniqueId: set.uniqueId,
      label: set.label,
      seriesSet: set,
      summarySet,
    };
  });
}

/**
 * Returns an array of null values to use as a filler when there is no summary data set
 * @param chartData
 * @returns
 */
export function getNullFillerRow(chartData?: ChartData): number[] {
  const [firstSet] = chartData?.dataSets || [];
  if (!firstSet) {
    return [];
  }
  return Array(firstSet.values.raw.length).fill(null);
}
